import { captureException, captureMessage } from '@sentry/react';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ActivityItem } from 'app/data/types/activity';
import { useSubmitFeedbackMutation } from 'app/data/mutations/use-submit-feedback-mutation';
import { useUnratedFeedbackCandidatesQuery } from 'app/data/queries/use-unrated-feedback-candidates-query';
import { ActivityFeedback, UnratedFeedbackCandidate } from 'app/data/types/feedback';

/**
 * The routing ids that identify this item's own RECORD — the only ones a candidate can be keyed on.
 *
 * `bookingUuid` is the booking-details uuid feedback-service stores for a RESOURCE_BOOKING,
 * `workOrderUuid` the work order, `eventUuid` the event.
 *
 * A whitelist, not every routing value, because the rest are not identities. `resourceUuid` and
 * `buildingUuid` are shared by every booking of the same desk or building, so matching on them would
 * hang one candidate off many items — the wrong card shows stars, and rating it burns the candidate
 * belonging to a different booking. `transactionUuid` is a real identity but the wrong one: it names
 * the transaction, where feedback-service keys a booking on its details uuid.
 */
const RECORD_ROUTING_KEYS = ['bookingUuid', 'workOrderUuid', 'eventUuid'] as const;

const recordUuidsOf = (item: ActivityItem): string[] =>
  (item.actions ?? []).flatMap((action) =>
    RECORD_ROUTING_KEYS.map((key) => action.routingDetails?.[key]).filter((uuid): uuid is string => !!uuid),
  );

/**
 * Candidates by every id they can be found under.
 *
 * Both keys, because on SERVICE_BOOKING `transaction_uuid` is a booking uuid from one producer and an
 * event uuid from the other — and the feed keys its own event documents both ways to match. Indexing
 * on one of them drops whichever producer disagrees, which reads as "ratings just don't work on some
 * events". `entity_uuid` is null on SERVICE_REQUEST and on anything minted before that column shipped.
 */
const indexByRecord = (candidates: UnratedFeedbackCandidate[]): Map<string, UnratedFeedbackCandidate> => {
  const index = new Map<string, UnratedFeedbackCandidate>();

  candidates.forEach((candidate) => index.set(candidate.transaction_uuid, candidate));

  candidates.forEach((candidate) => {
    // SERVICE_BOOKING only. `entity_uuid` is interpreted by vertical: the EVENT on a service booking,
    // which identifies the thing rated, but the RESOURCE on a resource booking — shared by every
    // booking of that desk, and so a category rather than an identity. Indexing that would offer one
    // candidate on several bookings and let a rating land against the wrong one.
    if (candidate.feature_vertical !== 'SERVICE_BOOKING' || !candidate.entity_uuid) {
      return;
    }

    // Second pass, and only into a free slot: a transaction key names this exact record, while an
    // event key is shared by every booking of that event, so it must never displace one.
    if (!index.has(candidate.entity_uuid)) {
      index.set(candidate.entity_uuid, candidate);
    }
  });

  return index;
};

interface SubmitParams {
  item: ActivityItem;
  rating: number;
  comment?: string;
}

export interface FeedbackContextValue {
  /** The item's feedback, or null when it has none outstanding and none given. Gates the stars. */
  feedbackFor: (item: ActivityItem) => ActivityFeedback | null;
  /** The rating to render for an item — 0 when no feedback has been given. */
  ratingFor: (item: ActivityItem) => number;
  /** Once true the stars lock: feedback is given once and never edited. */
  isSubmitted: (item: ActivityItem) => boolean;
  submit: (params: SubmitParams) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

/**
 * Owns "has this item been rated, and with what".
 *
 * Matched here rather than joined upstream. The feed does not carry feedback and is not going to: a
 * read-time join would put feedback-service on the critical path of the one endpoint the whole miniapp
 * depends on, to decorate it with stars. Fetched alongside instead, the feed renders on its own
 * schedule and feedback-service being slow or down costs stars rather than activity.
 *
 * Submitting is optimistic and **rolled back on failure**. With real candidates for every vertical a
 * failed write is now exceptional rather than expected, so leaving the stars filled would tell the user
 * their rating landed when it did not.
 *
 * Known gap, and not a regression: a candidate drops out of the unrated list the moment it is rated, so
 * a rating given in an earlier session cannot be read back and its stars are not rendered at all. That
 * is the endpoint enforcing no-re-rating; showing the given rating would need a second read that does
 * not exist yet.
 */
export const FeedbackProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [sessionRatings, setSessionRatings] = useState<Record<string, number>>({});
  // Synchronous companion to `sessionRatings`. The read-only lock is render-driven, so two taps inside
  // one tick would both see "not yet rated" and POST twice — the second earning a 400. A ref closes
  // that window because it is set before the re-render, not by it.
  const claimed = useRef<Set<string>>(new Set());
  const { mutate } = useSubmitFeedbackMutation();
  const { candidates } = useUnratedFeedbackCandidatesQuery();
  const candidatesByRecord = useMemo(() => indexByRecord(candidates), [candidates]);

  const feedbackFor = useCallback(
    (item: ActivityItem): ActivityFeedback | null => {
      // An explicit one wins: component specs express "this item has a candidate" this way, and a
      // non-search fallback feed could carry it.
      if (item.feedback) {
        return item.feedback;
      }

      const candidate = recordUuidsOf(item)
        .map((uuid) => candidatesByRecord.get(uuid))
        .find(Boolean);

      if (!candidate) {
        return null;
      }

      return {
        candidateUuid: candidate.uuid,
        // Now inline on the list response, off the settings row it already joins — which is what
        // retired the dependent per-candidate read this used to wait on, and its "assume true while
        // unknown" fallback with it. There is no longer an unknown state to fail toward.
        commentsEnabled: candidate.comments_enabled,
      };
    },
    [candidatesByRecord],
  );

  const ratingFor = useCallback(
    (item: ActivityItem): number => feedbackFor(item)?.rating ?? sessionRatings[item.id] ?? 0,
    [feedbackFor, sessionRatings],
  );

  const isSubmitted = useCallback((item: ActivityItem): boolean => ratingFor(item) > 0, [ratingFor]);

  const submit = useCallback(
    ({ item, rating, comment }: SubmitParams): void => {
      if (claimed.current.has(item.id)) {
        return;
      }

      claimed.current.add(item.id);

      // Optimistic first: the stars answer the tap, not the network.
      setSessionRatings((previous) => ({ ...previous, [item.id]: rating }));

      const candidateUuid = feedbackFor(item)?.candidateUuid;

      if (!candidateUuid) {
        // No candidate to submit against yet. Reported rather than dropped, so "nobody can rate
        // anything" stays visible instead of looking like a working write.
        captureMessage('feedback: rated an item with no candidate', {
          level: 'info',
          extra: { itemId: item.id, activityType: item.type },
        });

        return;
      }

      mutate(
        { candidateUuid, rating, comment },
        {
          onError: (error) => {
            captureException(error, { extra: { candidateUuid, itemId: item.id } });

            // Put the stars back. The rating did not land, and leaving them filled says it did — the
            // user would have no way to tell, and no way to try again, since the claim below would
            // keep refusing them.
            setSessionRatings((previous) => {
              const { [item.id]: _discarded, ...rest } = previous;

              return rest;
            });
            claimed.current.delete(item.id);
          },
        },
      );
    },
    [mutate, feedbackFor],
  );

  const value = useMemo(
    () => ({ feedbackFor, ratingFor, isSubmitted, submit }),
    [feedbackFor, ratingFor, isSubmitted, submit],
  );

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
};

export const useFeedback = (): FeedbackContextValue => {
  const context = useContext(FeedbackContext);

  // Throws rather than degrading to inert stars: a missing provider would look exactly like a rating
  // that silently does nothing, which is the hardest version of this bug to notice.
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }

  return context;
};

export { FeedbackContext };
